import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { Newspaper, ArrowRight, MapPin, CalendarDays, X, Rss, MessageSquare, Eye, Share2 } from "lucide-react";

const ADMIN_ROLES = ["admin", "super_admin", "manager"];

export default function PressReview() {
  const { user } = useAuth();
  const isAdmin = !!user && user !== false && ADMIN_ROLES.includes(user.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState("");
  const [stats, setStats] = useState(null);
  const [sortBy, setSortBy] = useState(null);

  useSeo({
    title: "Actu Chartres & Eure-et-Loir — Revue de presse locale | PROXIreduc-28",
    description: "L'essentiel de l'actualité locale à Chartres et en Eure-et-Loir, mis à jour chaque matin : faits du jour, vie locale et bons plans près de chez vous.",
    canonicalPath: "/actu",
    image: "/og-blog.jpg",
  });

  useEffect(() => {
    api.get("/press-review?limit=120").then((r) => setItems(r.data.items || [])).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (isAdmin) api.get("/admin/press-review/stats?days=30").then((r) => setStats(r.data)).catch(() => {});
  }, [isAdmin]);

  const cities = useMemo(
    () => Array.from(new Set(items.map((p) => p.city).filter(Boolean))).sort((a, b) => a.localeCompare(b, "fr")),
    [items]
  );
  const filtered = useMemo(() => items.filter((p) => !city || p.city === city), [items, city]);
  const displayed = useMemo(() => {
    if (!sortBy) return filtered;
    const key = { views: "views", comments: "comment_count", shares: "shares" }[sortBy];
    return [...filtered].sort((a, b) => (b[key] || 0) - (a[key] || 0));
  }, [filtered, sortBy]);

  return (
    <div data-testid="press-review-page">
      <section className="bg-slate-900 border-b border-slate-800 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#FF7A4D]">
            <Rss size={14} /> Revue de presse locale
          </div>
          <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-extrabold">
            Actu Chartres & Eure-et-Loir
          </h1>
          <p className="mt-3 text-base text-slate-300 max-w-2xl">
            Chaque matin, l'essentiel de l'actualité qui fait parler près de chez vous — résumé, illustré
            et accompagné du lien vers la source. L'info locale, en un coup d'œil.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {isAdmin && stats && (
          <div className="mb-8 rounded-2xl border border-slate-200 bg-white p-4" data-testid="press-admin-dashboard">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
              Performance · 30 derniers jours · {stats.articles} article{stats.articles > 1 ? "s" : ""}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <button type="button" onClick={() => setSortBy((s) => s === "views" ? null : "views")} title="Trier par ouvertures"
                className={`rounded-xl p-3 text-center transition-all ${sortBy === "views" ? "bg-rose-50 ring-2 ring-[#E05638]" : "bg-slate-50 hover:bg-slate-100"}`} data-testid="press-dash-views">
                <div className="flex items-center justify-center gap-1.5 text-slate-400 text-xs font-semibold"><Eye size={13} /> Ouvertures</div>
                <div className="mt-1 font-heading text-2xl font-extrabold text-slate-900">{stats.views.toLocaleString("fr-FR")}</div>
              </button>
              <button type="button" onClick={() => setSortBy((s) => s === "comments" ? null : "comments")} title="Trier par commentaires"
                className={`rounded-xl p-3 text-center transition-all ${sortBy === "comments" ? "bg-rose-50 ring-2 ring-[#E05638]" : "bg-slate-50 hover:bg-slate-100"}`} data-testid="press-dash-comments">
                <div className="flex items-center justify-center gap-1.5 text-slate-400 text-xs font-semibold"><MessageSquare size={13} /> Commentaires</div>
                <div className="mt-1 font-heading text-2xl font-extrabold text-slate-900">{stats.comments.toLocaleString("fr-FR")}</div>
              </button>
              <button type="button" onClick={() => setSortBy((s) => s === "shares" ? null : "shares")} title="Trier par partages"
                className={`rounded-xl p-3 text-center transition-all ${sortBy === "shares" ? "bg-rose-50 ring-2 ring-[#E05638]" : "bg-slate-50 hover:bg-slate-100"}`} data-testid="press-dash-shares">
                <div className="flex items-center justify-center gap-1.5 text-slate-400 text-xs font-semibold"><Share2 size={13} /> Partages</div>
                <div className="mt-1 font-heading text-2xl font-extrabold text-slate-900">{stats.shares.toLocaleString("fr-FR")}</div>
              </button>
            </div>
            {sortBy && (
              <button onClick={() => setSortBy(null)} data-testid="press-sort-reset"
                className="mt-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#E05638]">
                <X size={12} /> Tri actif : {sortBy === "views" ? "ouvertures" : sortBy === "comments" ? "commentaires" : "partages"} — réinitialiser
              </button>
            )}
          </div>
        )}
        {!loading && cities.length > 0 && (
          <div className="mb-8" data-testid="press-filters">
            <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
              <MapPin size={12} /> Par commune
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setCity("")} data-testid="press-city-all"
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${!city ? "bg-[#E05638] text-white border-[#E05638]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"}`}>
                Tout l'Eure-et-Loir
              </button>
              {cities.map((c) => (
                <button key={c} onClick={() => setCity(c)} data-testid={`press-city-${c}`}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${city === c ? "bg-[#E05638] text-white border-[#E05638]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"}`}>
                  {c}
                </button>
              ))}
            </div>
            {city && (
              <button onClick={() => setCity("")} data-testid="press-reset-filters"
                className="mt-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#E05638] transition-colors">
                <X size={14} /> Réinitialiser
              </button>
            )}
          </div>
        )}

        {loading ? (
          <div className="text-slate-500">Chargement…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-500" data-testid="press-empty">
            La revue de presse du jour arrive très bientôt. Revenez demain matin !
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="press-grid">
            {displayed.map((p) => (
              <Link key={p.id} to={`/actu/${p.slug}`} data-testid={`press-card-${p.slug}`}
                className="group bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all">
                <div className="aspect-[1.91/1] bg-slate-100 overflow-hidden">
                  {p.cover_image
                    ? <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    : <div className="w-full h-full grid place-items-center text-[#E05638]/40"><Newspaper size={40} /></div>}
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                    {p.city && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {p.city}</span>}
                    {p.published_at && <span className="inline-flex items-center gap-1"><CalendarDays size={12} /> {new Date(p.published_at).toLocaleDateString("fr-FR")}</span>}
                    {p.comment_count > 0 && <span className="inline-flex items-center gap-1 text-[#E05638] font-semibold" data-testid={`press-card-comments-${p.slug}`}><MessageSquare size={12} /> {p.comment_count}</span>}
                  </div>
                  <h2 className="mt-2 font-heading text-lg font-bold text-slate-900 group-hover:text-[#E05638] transition-colors line-clamp-2">{p.title}</h2>
                  {p.excerpt && <p className="mt-1.5 text-sm text-slate-600 line-clamp-3">{p.excerpt}</p>}
                  {p.source_name && <div className="mt-2 text-[11px] text-slate-400">Source : {p.source_name}</div>}
                  {isAdmin && (
                    <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] font-semibold" data-testid={`press-admin-stats-${p.slug}`}>
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-600 px-2 py-0.5" title="Ouvertures" data-testid={`press-stat-views-${p.slug}`}>
                        <Eye size={12} /> {p.views || 0}
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-600 px-2 py-0.5" title="Commentaires" data-testid={`press-stat-comments-${p.slug}`}>
                        <MessageSquare size={12} /> {p.comment_count || 0}
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-600 px-2 py-0.5" title="Partages" data-testid={`press-stat-shares-${p.slug}`}>
                        <Share2 size={12} /> {p.shares || 0}
                      </span>
                    </div>
                  )}
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#E05638]">Lire l'actu <ArrowRight size={14} /></span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
